// One Euro Filter (Casiez, Roussel & Vogel, 2012) — pure, no DOM, no Date.
// Speed-adaptive low-pass: at low velocity the cutoff frequency drops toward
// `minCutoff` (heavier smoothing, kills hand jitter on slow careful curves);
// at high velocity the cutoff rises with `beta` (near pass-through, so a fast
// flick keeps low lag under the finger). Callers supply real timestamps
// (seconds) from the input events — this module never reads the clock itself.

/** cutoff (Hz) used when the signal is nearly still */
const MIN_CUTOFF = 1.0;
/** how fast the cutoff rises with speed — higher = less lag when moving fast.
 *  Deliberately loose: this filter's job is the jitter of a nearly-still hand,
 *  and the tremor of a *moving* one is taken out downstream in the arc-length
 *  domain instead (`smoothStroke`, src/input/ink.ts), where it costs no lag.
 *  So the stroke can stay close under the finger while it is being drawn. */
const BETA = 0.05;
/** cutoff (Hz) for the velocity estimate itself. The canonical 1.0 makes the
 *  speed estimate take ~150 ms to catch up, so the *start* of a quick stroke is
 *  filtered as if the hand were still and the line lags ~19 px behind it before
 *  settling. 5 Hz gets the estimate up to speed in ~30 ms; measured residual on
 *  a still hand is unchanged (0.11 px vs 0.10 px of a +-1.5 px shake), because
 *  a hand that isn't moving has no speed to estimate either way. */
const D_CUTOFF = 5.0;
/** floor on the inferred sample interval (s) — guards duplicate/zero timestamps */
const MIN_DT = 1 / 1000;

function smoothingFactor(rate: number, cutoff: number): number {
  const tau = 1 / (2 * Math.PI * cutoff);
  const te = 1 / rate;
  return 1 / (1 + tau / te);
}

class LowPass {
  private y = 0;
  private has = false;
  filter(x: number, a: number): number {
    this.y = this.has ? a * x + (1 - a) * this.y : x;
    this.has = true;
    return this.y;
  }
  reset(): void { this.has = false; this.y = 0; }
}

/** One Euro filter for a single scalar channel. */
export class OneEuroFilter {
  private readonly xFilt = new LowPass();
  private readonly dxFilt = new LowPass();
  private lastX: number | null = null;
  private lastT: number | null = null;

  constructor(
    private readonly minCutoff = MIN_CUTOFF,
    private readonly beta = BETA,
    private readonly dCutoff = D_CUTOFF,
  ) {}

  /** `t` is a monotonic timestamp in seconds (e.g. `event.timeStamp / 1000`). */
  filter(x: number, t: number): number {
    const dt = this.lastT === null ? MIN_DT : Math.max(t - this.lastT, MIN_DT);
    const rate = 1 / dt;
    this.lastT = t;
    const dx = this.lastX === null ? 0 : (x - this.lastX) * rate;
    this.lastX = x;
    const edx = this.dxFilt.filter(dx, smoothingFactor(rate, this.dCutoff));
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    return this.xFilt.filter(x, smoothingFactor(rate, cutoff));
  }

  reset(): void {
    this.xFilt.reset();
    this.dxFilt.reset();
    this.lastX = null;
    this.lastT = null;
  }
}

/** Convenience pair of independent x/y One Euro filters for a 2D point stream. */
export class OneEuroFilter2D {
  private readonly fx = new OneEuroFilter();
  private readonly fy = new OneEuroFilter();

  filter(x: number, y: number, t: number): { x: number; y: number } {
    return { x: this.fx.filter(x, t), y: this.fy.filter(y, t) };
  }

  reset(): void {
    this.fx.reset();
    this.fy.reset();
  }
}
